import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link, useNavigate } from "react-router-dom";
import { apiCall } from "../Apis/apiCall";
import LoadingComponent from "./ui/LoadingComponent";
import PaginationControls from "./ui/PaginationControls";
import PerPageDropdown from "./ui/PerPageDropdown";
import { usePermissions } from "../context/PermissionContext";
import { handleApiResponse, handleCustomResponse } from "../utils/response";
import SortIcons from "./ui/SortIcons";
import ErrorAlert from "./ui/ErrorAlert";

const WorkFLowAssignLayer = () => {
  const navigate = useNavigate();

  // State management
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalEntries, setTotalEntries] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "assignment_id",
    direction: "desc",
  });
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("");
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [searchTimeout, setSearchTimeout] = useState(null);
  const { hasPermission } = usePermissions();

  // Truncate text to a specified length
  const truncateText = (text, maxLength = 50) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  // Toggle expanded state for a row and field
  const toggleExpand = (workflowId, field) => {
    setExpandedRows((prev) => ({
      ...prev,
      [`${workflowId}_${field}`]: !prev[`${workflowId}_${field}`],
    }));
  };

  // Fetch workflows from API
  const fetchWorkflows = async (
    page = 1,
    search = "",
    sortBy = "assignment_id",
    sortOrder = "desc",
    entriesPerPage = perPage
  ) => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        page,
        per_page: entriesPerPage,
        sort_by: sortBy,
        sort_order: sortOrder,
      };

      if (search.trim()) {
        params.search = search.trim();
      }

      const response = await apiCall(
        "GET",
        "/get-all-assigned-workflows",
        params,
        navigate
      );

      if (response?.data?.data) {
        const { data, current_page, per_page, total, last_page } =
          response.data.data;
        setWorkflows(data || []);
        setCurrentPage(current_page || 1);
        setPerPage(per_page || 10);
        setTotalEntries(total || 0);
        setTotalPages(last_page || 0);
      } else {
        setWorkflows([]);
        setError("No data returned from the server.");
      }
    } catch (err) {
      console.error("API Error:", err);
      setError(err.message || "Failed to fetch workflows");
      setWorkflows([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchWorkflows(1, searchQuery, sortConfig.key, sortConfig.direction);
  }, []);

  // Handle delete workflow
  const handleDeleteWorkflow = async () => {
    if (!selectedWorkflow?.assignment_id && !selectedWorkflow?.id) {
      handleCustomResponse("error", "Invalid or missing Workflow ID.");
      return;
    }

    setDeleteLoading(true);
    try {
      const response = await apiCall(
        "DELETE",
        `/delete-workflow-assignment/${
          selectedWorkflow.assignment_id || selectedWorkflow.id
        }`,
        null,
        navigate
      );

      handleApiResponse(response.data, "success", "DELETE");
      await fetchWorkflows(
        currentPage,
        searchQuery,
        sortConfig.key,
        sortConfig.direction
      );
      handleCloseModal();
    } catch (error) {
      handleApiResponse(error.response?.data, "Err", "DELETE");
    } finally {
      setDeleteLoading(false);
    }
  };

  // Open delete confirmation modal
  const handleOpenDeleteModal = (workflow) => {
    setSelectedWorkflow(workflow);
    setModalType("delete");
    setShowModal(true);
  };

  // Handle search with debounce
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    // setLoading(true); // Show loader during debounce

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      setCurrentPage(1);
      fetchWorkflows(1, value, sortConfig.key, sortConfig.direction);
    }, 500); // Match AllClients.jsx debounce timing

    setSearchTimeout(timeout);
  };

  // Handle entries per page change
  const handleEntriesPerPageChange = (value) => {
    setPerPage(value);
    setCurrentPage(1);
    fetchWorkflows(1, searchQuery, sortConfig.key, sortConfig.direction, value);
  };

  // Handle sorting
  const handleSort = (key) => {
    const direction =
      sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
    setSortConfig({ key, direction });
    setCurrentPage(1);
    fetchWorkflows(1, searchQuery, key, direction);
  };

  // Handle pagination
  const handlePageChange = (page) => {
    if (page !== currentPage && page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      fetchWorkflows(page, searchQuery, sortConfig.key, sortConfig.direction);
    }
  };

  // Handle view workflow
  const handleViewWorkflow = (workflow) => {
    setSelectedWorkflow(workflow);
    setModalType("view");
    setShowModal(true);
  };

  // Handle close modal
  const handleCloseModal = () => {
    setShowModal(false);
    setModalType("");
    setSelectedWorkflow(null);
  };

  // Get page numbers for pagination
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);

    if (endPage - startPage < maxPagesToShow - 1) {
      if (startPage === 1) {
        endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
      } else if (endPage === totalPages) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  };

  // Map API field names to display names for sorting
  const getSortIcon = (field) => {
    const apiFieldMap = {
      workflow: "workflow_name",
      assigned_to: "assigned_user",
      department: "department",
      email: "email",
      phone: "phone",
      created_by: "assigned_by",
      created_date: "assigned_at",
      client: "client_name",
      workflow_id: "workflow_id",
      client_id: "client_id",
    };
    return sortConfig.key === (apiFieldMap[field] || field);
  };

  if (error) {
    return <ErrorAlert />;
  }
  return (
    <div className="p-4 p-md-3 p-sm-2">
      <style>
        {`
          .table-responsive .table td, .table-responsive .table th {
            max-width: 200px;
            vertical-align: middle;
          }
          .show-more-btn {
            color: #E63C23;
            cursor: pointer;
            font-size: 12px;
            margin-left: 5px;
            background: none;
            border: none;
            padding: 0;
          }
          .show-more-btn:hover {
            text-decoration: underline;
          }
          @media (max-width: 576px) {
            .table-responsive .table td, .table-responsive .table th {
              max-width: 100px;
            }
            .show-more-btn {
              font-size: 10px;
            }
          }
        `}
      </style>

      <div className="card shadow-sm">
        <div className="card p-3 p-sm-2 p-md-3">
          <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
            <div
              className="icon-field flex-grow-1"
              style={{ maxWidth: "400px" }}
            >
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search By Workflow Name"
                value={searchQuery}
                onChange={handleSearchChange}
              />
              <span className="icon">
                <Icon icon="ion:search-outline" />
              </span>
            </div>
            <PerPageDropdown
              perPage={perPage}
              handlePerPageChange={handleEntriesPerPageChange}
            />
            <div className="ms-auto">
              {hasPermission("assign_workflow") && (
                <Link
                  to="/assign-workflow"
                  className="btn btn-primary-600 btn-sm d-flex align-items-center"
                >
                  <Icon icon="ri:add-line" className="me-2" /> Assign Workflow
                </Link>
              )}
            </div>
          </div>

          {error && (
            <div
              className="alert alert-danger alert-dismissible fade show mb-3"
              role="alert"
            >
              {error}
              <button
                type="button"
                className="btn-close"
                onClick={() => setError(null)}
              ></button>
            </div>
          )}

          {workflows.length === 0 && !loading && !error ? (
            <div className="alert alert-warning" role="alert">
              No workflows found.
            </div>
          ) : (
            <div className="position-relative">
              <div
                className="table-responsive"
                style={{
                  opacity: loading ? 0.4 : 1,
                  pointerEvents: loading ? "none" : "auto",
                }}
              >
                <table className="table bordered-table mb-0 table-hover">
                  <thead>
                    <tr style={{ backgroundColor: "#475EAB", color: "white" }}>
                      <th scope="col" className="p-2">
                        <span>S.No</span>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("workflow")}
                        className="p-2"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Workflow Name</span>
                          <SortIcons
                            field="workflow_name"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("client")}
                        className="p-2"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Client Name</span>
                          <SortIcons
                            field="client_name"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("created_date")}
                        className="p-2 d-lg-table-cell"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Assigned Date</span>
                          <SortIcons
                            field="assigned_at"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th scope="col" className="p-2">
                        <span>Action</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {workflows.map((workflow, index) => (
                      <tr key={workflow.assignment_id || workflow.id || index}>
                        <td className="p-2">
                          {(currentPage - 1) * perPage + index + 1}
                        </td>
                        <td className="p-2">
                          <span className="">
                            {expandedRows[
                              `${
                                workflow.assignment_id || workflow.id
                              }_workflow_name`
                            ]
                              ? workflow.workflow_name || "-"
                              : truncateText(workflow.workflow_name || "-")}
                            {workflow.workflow_name &&
                              workflow.workflow_name.length > 50 && (
                                <button
                                  className="show-more-btn"
                                  onClick={() =>
                                    toggleExpand(
                                      workflow.assignment_id || workflow.id,
                                      "workflow_name"
                                    )
                                  }
                                >
                                  {expandedRows[
                                    `${
                                      workflow.assignment_id || workflow.id
                                    }_workflow_name`
                                  ]
                                    ? "Show Less"
                                    : "Show More"}
                                </button>
                              )}
                          </span>
                        </td>
                        <td className="p-2">
                          <span className="">
                            {expandedRows[
                              `${
                                workflow.assignment_id || workflow.id
                              }_client_name`
                            ]
                              ? workflow.client_name || "-"
                              : truncateText(workflow.client_name || "-")}
                            {workflow.client_name &&
                              workflow.client_name.length > 50 && (
                                <button
                                  className="show-more-btn"
                                  onClick={() =>
                                    toggleExpand(
                                      workflow.assignment_id || workflow.id,
                                      "client_name"
                                    )
                                  }
                                >
                                  {expandedRows[
                                    `${
                                      workflow.assignment_id || workflow.id
                                    }_client_name`
                                  ]
                                    ? "Show Less"
                                    : "Show More"}
                                </button>
                              )}
                          </span>
                        </td>
                        <td className="p-2 d-lg-table-cell">
                          {workflow.assigned_at
                            ? new Date(
                                workflow.assigned_at
                              ).toLocaleDateString()
                            : "-"}
                        </td>
                        <td className="p-2">
                          {hasPermission("delete_assign_workflow") && (
                            <button
                              className="w-32-px h-32-px me-8 bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                              onClick={() => handleOpenDeleteModal(workflow)}
                              style={{
                                position: "relative",
                                transition: "all 0.3s ease",
                                cursor: "pointer",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = "scale(1.1)";
                                const tooltip = document.createElement("div");
                                tooltip.textContent = "Delete";
                                tooltip.style.cssText = `
                                  position: absolute;
                                  top: -35px;
                                  left: 50%;
                                  transform: translateX(-50%);
                                  background-color: #333;
                                  color: white;
                                  padding: 5px 8px;
                                  border-radius: 4px;
                                  font-size: 12px;
                                  white-space: nowrap;
                                  z-index: 1000;
                                  pointer-events: none;
                                `;
                                tooltip.id = "delete-tooltip";
                                e.currentTarget.appendChild(tooltip);
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = "scale(1)";
                                const tooltip =
                                  document.getElementById("delete-tooltip");
                                if (tooltip) tooltip.remove();
                              }}
                            >
                              <Icon icon="mingcute:delete-2-line" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {loading && (
                <div
                  className="position-absolute top-50 start-50 translate-middle d-flex justify-content-center align-items-center"
                  style={{
                    zIndex: 10,
                    borderRadius: "10px",
                    padding: "20px",
                  }}
                >
                  <LoadingComponent />
                </div>
              )}

              <PaginationControls
                pagination={{ currentPage, perPage, total: totalEntries }}
                handlePageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {modalType === "view"
                    ? `Workflow Details - #${
                        selectedWorkflow?.assignment_id || selectedWorkflow?.id
                      }`
                    : "Confirm Delete Workflow"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseModal}
                ></button>
              </div>
              <div className="modal-body">
                {modalType === "view" && selectedWorkflow ? (
                  <table className="table text-sm text-secondary-light w-100">
                    <tbody>
                      <tr>
                        <td className="pe-2 fw-medium">Assignment ID:</td>
                        <td>
                          {selectedWorkflow.assignment_id ||
                            selectedWorkflow.id}
                        </td>
                      </tr>
                      <tr>
                        <td className="pe-2 fw-medium">Workflow Name:</td>
                        <td>{selectedWorkflow.workflow_name || "-"}</td>
                      </tr>
                      <tr>
                        <td className="pe-2 fw-medium">Client Name:</td>
                        <td>{selectedWorkflow.client_name || "-"}</td>
                      </tr>
                      <tr>
                        <td className="pe-2 fw-medium">Assigned User:</td>
                        <td>{selectedWorkflow.assigned_user || "-"}</td>
                      </tr>
                      <tr>
                        <td className="pe-2 fw-medium">Workflow ID:</td>
                        <td>{selectedWorkflow.workflow_id || "-"}</td>
                      </tr>
                      <tr>
                        <td className="pe-2 fw-medium">Client ID:</td>
                        <td>{selectedWorkflow.client_id || "-"}</td>
                      </tr>
                      <tr>
                        <td className="pe-2 fw-medium">Assigned By:</td>
                        <td>{selectedWorkflow.assigned_by || "-"}</td>
                      </tr>
                      <tr>
                        <td className="pe-2 fw-medium">Assigned At:</td>
                        <td>
                          {selectedWorkflow.assigned_at
                            ? new Date(
                                selectedWorkflow.assigned_at
                              ).toLocaleDateString()
                            : "-"}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                ) : modalType === "delete" ? (
                  <p>
                    Are you sure you want to delete{" "}
                    <strong>
                      {selectedWorkflow?.workflow_name || "this workflow"}
                    </strong>
                    ?
                  </p>
                ) : (
                  <p>No workflow data available.</p>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={handleCloseModal}
                >
                  {modalType === "view" ? "Close" : "Cancel"}
                </button>
                {modalType === "delete" && (
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={handleDeleteWorkflow}
                    disabled={deleteLoading}
                  >
                    {deleteLoading ? "Please wait..." : "Confirm Delete"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkFLowAssignLayer;
